import { useWeb3React } from "@web3-react/core";
import { useLocalStorage, useUpdate } from "react-use";
import { BigNumber, ethers, utils } from "ethers";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NFTAddr, serverPath } from "../config";
import { SofaNFT } from "./contract/SofaNFT";
import { formatDashboard } from "../utils/format";
import { MerkleRet, STAGES, int } from "../types";
import axios from "axios";
import { formatError } from "../utils";
import showAlert from "../components/alert";
axios.defaults.baseURL = serverPath;

interface iMerkleProof {
  index: number;
  account: string;
  merkleProof: string[];
}

export type Dashboard = SofaNFT.ContractDashboardStruct;

export function useContract(queryTick: number = 3000) {
  const { active, error, account, library, deactivate, connector } =
    useWeb3React();
  const update = useUpdate();
  const provider = useRef<ethers.providers.Web3Provider | null>(null);
  const statusRef = useRef<SofaNFT.ContractDashboardStructOutput>();

  const contract = useRef<SofaNFT | null>(null);

  const [loading, setLoading] = useState(false);
  const [interacting, setInteracting] = useState(false);

  const [publicPrice, setPublicPrice] = useState<BigNumber>();
  const [wlPrice, setWlPrice] = useState<BigNumber>();

  const [totalSupply, setTotalSupply] = useState(0);

  const [userSelectCount, setUserSelectCount] = useState(1);
  const totalSupplyRef = useRef(0);
  const [userWhiteList, setUserWhiteList] = useState({
    wl1: false,
    wl2: false,
  });
  const userMerkle = useRef<MerkleRet>(null);
  const [dashboard, setDashboard] =
    useState<SofaNFT.ContractDashboardStructOutput>();

  // 当前的价格
  const currentPrice = useMemo(() => {
    if (!statusRef.current) return 0;
    if (!dashboard) return 0;
    const st = statusRef.current;
    const stage = st.currentStage.toNumber();
    // auction price 判定
    if ([STAGES.AUCTION_NOT_START, STAGES.AUCTION_RUNNING].includes(stage)) {
      const data = findAuctionIndex(dashboard);
      const { time, index } = data;
      const priceAuction = dashboard.auction_price_list[index];
      // console.log("stage = ", stage, "price =", st.price_auction.toString());
      return priceAuction;
    }
    return st.price;
  }, [dashboard?.now, dashboard]);

  // 当前的价格
  const currentMaxCount = useMemo(() => {
    if (!statusRef.current) return 0;
    const st = statusRef.current;
    const stage = st.currentStage.toNumber();
    // console.log("stage = ", stage);
    // auction price 判定
    if ([STAGES.AUCTION_NOT_START, STAGES.AUCTION_RUNNING].includes(stage)) {
      return 5;
    }
    // 保证白
    if ([STAGES.AUCTION_END, STAGES.WL1_RUNNING].includes(stage)) {
      return 3;
    }
    // 白名单
    if ([STAGES.WL1_END, STAGES.WL2_RUNNING].includes(stage)) {
      return 3;
    }
    // 公售
    if (
      [STAGES.WL2_END, STAGES.PUBLIC_RUNNING, STAGES.PUBLIC_END].includes(stage)
    ) {
      return 3;
    }
    return 0;
  }, [dashboard]);

  useEffect(() => {
    if (active) {
      while (1) {
        const p = library as ethers.providers.Web3Provider;
        provider.current = p;

        contract.current = new ethers.Contract(
          NFTAddr,
          require("../web3/contract/SofaNFT.json").abi,
          p
        ).connect(p.getSigner()) as SofaNFT;
        // console.log(contract.current);
        break;
      }
      // console.log("active");
      queryData();
      //   publicMint(5);
    } else {
      contract.current = null;
    }
  }, [active]);

  useEffect(() => {
    if (currentMaxCount < userSelectCount) {
      setUserSelectCount(currentMaxCount);
    }
  }, [currentMaxCount]);

  useEffect(() => {
    const interval = setInterval(() => {
      queryData();
    }, queryTick);
    return () => {
      clearInterval(interval);
    };
  });

  useEffect(() => {
    if (active && account) {
      // queryUserWhiteList();
    }
  }, [active, account]);

  async function queryUserWhiteList() {
    const resp = await checkAddress(account!!);
    setUserWhiteList(resp);
  }

  const addressShortcut = useMemo(() => {
    return account == null
      ? "not connected"
      : account.slice(0, 4) + "..." + account.slice(-4);
  }, [account]);

  async function queryData() {
    // console.log("query");
    if (active && contract.current) {
      // console.log(account, contract.current.address);
      const status = await contract.current.status(account!!, Date.now());
      // console.log("status = ", formatDashboard(status));
      const stage = status.currentStage.toNumber();
      // console.log("stage = ", stage);
      statusRef.current = status;
      const finalAuctionPrice = BigNumber.from(
        await contract.current.finalAuctionPrice()
      );
      setDashboard(status);
      const auctionStartTime = BigNumber.from(status.auctionStart).toNumber();
      const now = Math.floor(Date.now() / 1000);
      // console.log(
      //   "auctionStart = ",
      //   auctionStartTime,
      //   "d = ",
      //   now - auctionStartTime,
      //   "block.timestamp =",
      //   BigNumber.from(status.now).toNumber()
      // );
      // const paused = await contract.current.paused();
      // console.log("paused =", paused);

      // const currentMint = status.totalSupply.toNumber();
      // totalSupplyRef.current = currentMint;
      // setTotalSupply(currentMint);
      // setPublicPrice(publicPrice);
      // setWlPrice(wlPrice);
    }
  }

  async function auctionMint() {
    if (interacting) return;
    setInteracting(true);
    try {
      const paused = await contract.current?.paused();
      if (paused) {
        return {
          tips: "mint not start yet!",
          isSuccess: false,
        };
      }
      const minted = BigNumber.from(dashboard!!.user_minted_auction).toNumber();

      if (minted + userSelectCount > currentMaxCount) {
        throw Error(
          `in current phase, you can not mint more than ${currentMaxCount}!`
        );
      }
      const price = BigNumber.from(dashboard?.price_auction || 0);
      const value = price.mul(userSelectCount);
      const tx = await contract.current!!.auctionMint(userSelectCount, {
        value,
      });
      const resp = await tx.wait();
      console.log(resp);
      // alert("mint success");
      return {
        tips: "",
        isSuccess: true,
      };
    } catch (e) {
      // alert("mint failed, reason : " + (e as Error).message);
      throw e;
    } finally {
      setInteracting(false);
    }
  }

  async function publicMint(amount: number) {
    if (interacting) return;
    setInteracting(true);
    try {
      const paused = await contract.current?.paused();
      if (paused) {
        throw Error("mint not start yet!");
      }
      const minted = BigNumber.from(dashboard!!.user_minted_public).toNumber();
      if (minted + userSelectCount > currentMaxCount) {
        throw Error(
          `in current phase, you can not mint more than ${currentMaxCount}!`
        );
      }
      const publicPrice = await contract.current!.wlPrice();
      const value = publicPrice.mul(amount);

      const tx = await contract.current!!.publicMint(amount, {
        value,
      });
      const resp = await tx.wait();
      console.log(resp);
      // alert("mint success");
      return {
        tips: "",
        isSuccess: true,
      };
    } catch (e) {
      // alert("mint failed, reason : " + (e as Error).message);
      throw e;
    } finally {
      setInteracting(false);
    }
  }

  interface proofResp {
    code: number;
    data: {
      index: number;
      address: number;
      proof: string[];
    } | null;
  }

  async function wlMint(amount: number, stage: number) {
    if (interacting) return;
    setInteracting(true);
    await queryData();
    try {
      const paused = await contract.current?.paused();
      const status = statusRef.current!!;
      if (paused) {
        return {
          tips: "mint not start yet!",
          isSuccess: false,
        };
      }
      const wlPrice = status.price;
      const value = wlPrice.mul(amount);

      const { merkle1: wl1, merkle2: wl2 } = await getMerkleProof(account!!);
      // 检查最大
      const proof = stage === 1 ? wl1 : wl2;
      const minted = BigNumber.from(
        stage === 1
          ? dashboard!!.user_minted_wl_1
          : dashboard!!.user_minted_wl_2
      ).toNumber();

      if (minted + amount > currentMaxCount) {
        throw Error(
          `in current phase, you can not mint more than ${currentMaxCount}!`
        );
      }

      if (proof == null) {
        // alert("sorry, u are not in whitelist");
        throw Error("sorry, u are not in whitelist");
      } else {
        const func =
          stage === 1
            ? contract.current!!.whitelist1Mint
            : contract.current!!.whitelistMint;
        const tx = await func(amount, proof.index, account!!, proof.proof, {
          value,
        });
        const resp = await tx.wait();
        console.log(resp);
        return {
          tips: "",
          isSuccess: true,
        };
      }
    } catch (e) {
      throw e;
      // alert("mint failed, reason : " + (e as Error).message);
    } finally {
      setInteracting(false);
    }
  }

  async function mint() {
    const stage = dashboard?.currentStage.toNumber();
    const { hide: hideLoadingAlert } = showAlert({
      title: "Minting",
      content: <div className="global-loading-content"></div>,
      customCls: 'no-corner-img',
    });
    try {
      let res: { tips: string; isSuccess: boolean } | undefined = undefined;
      if (stage === STAGES.AUCTION_RUNNING) {
        res = await auctionMint();
      } else if (stage === STAGES.WL1_RUNNING) {
        if (userWhiteList.wl1) {
          res = await wlMint(userSelectCount, 1);
        } else {
          throw Error("you are not in whitelist");
        }
      } else if (stage === STAGES.WL2_RUNNING) {
        if (userWhiteList.wl2) {
          res = await wlMint(userSelectCount, 2);
        } else {
          throw Error("you are not in whitelist");
        }
      } else if (stage === STAGES.PUBLIC_RUNNING) {
        res = await publicMint(userSelectCount);
      } else {
        throw Error("mint not start or finished!");
      }
      if (res && !res.isSuccess) {
        throw Error(res.tips);
      } else {
        hideLoadingAlert();
        showAlert({
          children: <div className="alert-content-mint-success"></div>,
          content: "",
        });
      }
    } catch (e) {
      hideLoadingAlert();
      showAlert({ title: "Mint Failed", content: formatError(e).message });
      setInteracting(false);
    } 
  }

  function logOut() {
    deactivate();
  }

  return {
    account,
    addressShortcut,
    active,
    contract,
    loading,
    interacting,
    publicPrice,
    wlPrice,
    totalSupply,
    totalSupplyRef,
    publicMint,
    wlMint,
    logOut,
    userSelectCount,
    setUserSelectCount,
    statusRef,
    currentPrice,
    currentMaxCount,
    dashboard,
    findAuctionIndex: () => findAuctionIndex(dashboard!!),
    userWhiteList,
    mint,
  };
}

export function findAuctionIndex(dashboard: Dashboard) {
  const now = Math.ceil(Date.now() / 1000);
  let index = 0;
  const timeList = dashboard!!.auction_time_list.map((x) =>
    BigNumber.from(x).toNumber()
  );
  const endTime = BigNumber.from(dashboard.auctionEnd).toNumber();
  const arr = timeList.map((x, i) => {
    if (i === timeList.length - 1) {
      return endTime;
    }
    return timeList[i + 1];
  });

  for (var i = 0; i < arr.length; i++) {
    if (arr[i] >= now) {
      index = i;
      break;
    }
  }

  let time = arr[index];

  return { index, time: time };
}

export interface iMetadata {
  name: string;
  image: string;
}

export async function checkAddress(address: string) {
  const resp = await axios.get<{ wl1: boolean; wl2: boolean }>(
    "/api/v2/check",
    {
      params: {
        address: address.toLowerCase(),
        t: Math.floor(Date.now() / 6000),
        version: 2,
      },
    }
  );
  return resp.data;
}

export async function getMerkleProof(address: string) {
  const resp = await axios.get<MerkleRet>("/api/v2/merkle", {
    params: {
      address: address.toLowerCase(),
      t: Math.floor(Date.now() / 6000),
      version: 2,
    },
  });
  return resp.data;
}
